/**
 * ReferenceTableFooter — the FK popover's on-ramp to (and off-ramp from)
 * "show names from the referenced table".
 *
 * The popover is the one place in the app where the user is already looking at
 * a key AND the row it points at, thinking "why do I have to open this to see
 * the name?". So it carries the answer: one button that flags the TARGET table,
 * after which every column referencing it shows `101 · Acme Corp` — this grid,
 * the other grid, and every table that references it tomorrow.
 *
 * The flag it writes is `mode: 'on-demand'` unconditionally, because the
 * popover asks no questions and runs no `COUNT(*)`: pairs are resolved per
 * visible grid window and never persisted, which is correct at any target size.
 * (The declare dialog's checkbox writes `'cached'` — it has a preview that
 * proved the whole pair set fits.) Both are the SAME concept, and the footer
 * deliberately introduces no new vocabulary for the difference.
 *
 * Rendered for real AND loose FKs, alongside `LooseRelationshipFooter` on the
 * loose ones — the question "should this target show names?" is the same either
 * way. Shared by BOTH grids through `ForeignKeyPopover` (CLAUDE.md § "Two
 * Results Grids").
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { toast } from '../../../../components/ui/toaster';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../core/engines';
import { useInvalidateMetaInfo, useMetaInfo } from '../../common/hooks/useMetaInfo';
import { removeReferenceTable } from '../../common/services/metaInfoApi';
import {
  confirmRemoveReferenceTable,
  declareReferenceTable,
} from '../../common/components/meta-info/referenceTableActions';
import { findReferenceTable } from '../../common/utils/effectiveLookupDeclaration';
import {
  LOOKUP_TARGET_COLUMNS_STALE_MS,
  fetchLookupTargetColumns,
  lookupTargetColumnsQueryKey,
  resolveLookupLabelColumn,
} from '../../common/utils/lookupLabelColumn';
import { qualifyLookupTarget } from '../../common/utils/lookupEnumTarget';

type ReferenceTableFooterProps = {
  readonly connectionId: string;
  readonly database: string;
  /** The FK target. `''` is a valid schema (schemaless engines). */
  readonly schema: string;
  readonly table: string;
  /** The referenced key column — never a candidate for labelling itself. */
  readonly keyColumn: string;
  readonly engine?: DatabaseEngineId;
  /** Closes the host popover once the flag is gone. */
  readonly onRemoved: () => void;
};

export function ReferenceTableFooter({
  connectionId,
  database,
  schema,
  table,
  keyColumn,
  engine,
  onRemoved,
}: ReferenceTableFooterProps) {
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  /** The flag write is in flight — the on-ramp button is not clickable twice. */
  const [declaring, setDeclaring] = useState(false);

  const flag = findReferenceTable(metaInfo, {
    toSchema: schema,
    toTable: table,
    toColumn: keyColumn,
  });
  const qualified = qualifyLookupTarget(schema, table);

  // Only asked once the flag exists — an unflagged target owes the user no
  // label column, and the on-ramp must not cost a metadata round-trip. Same
  // query key as the declare dialog / target picker / self-heal, so this
  // usually resolves from cache.
  const columnsQuery = useQuery({
    queryKey: lookupTargetColumnsQueryKey(connectionId, database, schema, table),
    queryFn: (): Promise<string[]> =>
      fetchLookupTargetColumns({ connectionId, database, schema, table }),
    enabled: !!flag,
    staleTime: LOOKUP_TARGET_COLUMNS_STALE_MS,
  });

  const labelColumn = useMemo(
    () =>
      flag
        ? resolveLookupLabelColumn({
            metaInfo,
            schema,
            table,
            keyColumn,
            columns: columnsQuery.data ?? [],
          })
        : null,
    [flag, metaInfo, schema, table, keyColumn, columnsQuery.data],
  );

  const handleShowNames = async () => {
    // The write is a round-trip; without this the user can land two clicks on
    // it and get two `addReferenceTable` calls, two declare events, and two
    // toasts each offering to undo a flag there is only one of.
    if (declaring) return;
    setDeclaring(true);
    try {
      await declareReferenceTable({
        connectionId,
        database,
        schema,
        table,
        engine,
        mode: 'on-demand',
        trigger: 'fk-popover',
        track,
        invalidate,
      });
    } catch (err) {
      toast.error({
        title: 'Could not show names',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
      return;
    } finally {
      setDeclaring(false);
    }
    toast.success({
      title: `Showing names from ${qualified}`,
      description: `Every column that references ${qualified} shows names.`,
      actions: [
        {
          label: 'Undo',
          testId: 'fk-popover-undo-show-names',
          // No confirm (the user is undoing something they did one second
          // ago, and the blast radius is what they just created) and NO
          // undeclare event: an Undo is a correction to the declare above it,
          // not a deliberate "stop showing names" — counting it as one would
          // make the two events indistinguishable on the dashboard. It gets
          // its OWN event instead, so "how often is the on-ramp an accident?"
          // is answerable rather than invisible.
          onClick: () => {
            void (async () => {
              try {
                await removeReferenceTable(connectionId, database, schema, table);
                await invalidate(connectionId, database);
                // `engine ?? 'unknown'` — same fallback the declare/undeclare
                // events use, so the three are comparable on one dashboard.
                track('feature:enum_reference_table_undo', { engine: engine ?? 'unknown' });
              } catch (err) {
                // The flag is still on and the grids are about to show names
                // the user just asked to take back — say so, exactly as the
                // declare path does when IT fails.
                toast.error({
                  title: 'Undo failed — the table is still showing names',
                  description: err instanceof Error ? err.message : 'Unknown error',
                });
              }
            })();
          },
        },
      ],
    });
  };

  const handleStopNames = () => {
    confirmRemoveReferenceTable({
      connectionId,
      database,
      schema,
      table,
      engine,
      mode: flag?.mode ?? 'cached',
      trigger: 'fk-popover',
      track,
      invalidate,
      onRemoved,
    });
  };

  if (!flag) {
    return (
      <div className="px-3 py-1.5 border-t border-border">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 -ml-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={handleShowNames}
          disabled={declaring}
          data-testid="fk-popover-show-names"
          title={`Every column that references ${qualified} shows names`}
        >
          <Tag className="h-3 w-3 mr-1" />
          Show names from {qualified}
        </Button>
        {/* Names the CONCEPT as well as the effect. The MetaInfo manager lists
            what this button writes under "Reference tables"; a sub-line that
            only described the effect left the user with no word to recognise
            their own entry by later. */}
        <div
          className="text-[10px] text-muted-foreground"
          data-testid="fk-popover-show-names-hint"
        >
          Marks {qualified} as a reference table — every column that references
          it shows names.
        </div>
      </div>
    );
  }

  return (
    // Two stacked rows, not one: sharing a row with the button ellipsized the
    // status line at the popover's width — hiding the target table and label
    // column, the exact information this footer exists to show (caught by the
    // screenshot audit; text assertions can't see it, only the render).
    <div className="px-3 py-1.5 border-t border-border flex flex-col gap-0.5">
      <span
        className="text-[11px] text-muted-foreground break-words min-w-0"
        data-testid="fk-popover-showing-names"
      >
        Showing names from{' '}
        <span className="font-mono" translate="no">
          {qualified}
        </span>
        {labelColumn ? (
          <>
            {' · '}
            <span className="font-mono" translate="no">
              {labelColumn}
            </span>
          </>
        ) : columnsQuery.isLoading ? null : columnsQuery.isError ? (
          // A failed metadata read is not evidence the target has no name
          // column — sending the user to go set one would have them fix a
          // setting that is probably already right.
          <> · couldn&apos;t load the label column</>
        ) : (
          <> · no label column — set one in the enum details or MetaInfo manager</>
        )}
      </span>
      <div className="flex justify-end">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 shrink-0 text-xs text-muted-foreground hover:text-destructive"
        onClick={handleStopNames}
        data-testid="fk-popover-stop-names"
        title={`Stop showing names from ${qualified} everywhere`}
      >
        Stop showing names
      </Button>
      </div>
    </div>
  );
}
